import type { ActivityEvent, Execution, Message, WorkItem, WorkItemDetail } from '@deskworks/shared-types';
import { ACTIVE_EXECUTION_STATUSES } from '@deskworks/shared-types';
import { ArrowRight, ChevronRight, Clock, Loader2, ShieldCheck } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { activityIcon } from '@/components/activity-feed';
import { AgentAvatar } from '@/components/agent-avatar';
import { AgentSteps } from '@/components/agent-steps';
import { Markdown } from '@/components/markdown';
import { SessionLink } from '@/components/session-link';
import { formatAbsolute, formatDuration, formatTime } from '@/lib/format';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * The course of a Work Item by run, newest first (docs/features/work-item-chat.md#the-course-by-run): each run of an
 * agent with what it was asked to do, what it found, what happened along the way and what followed — the Lead taking
 * the result in and whom it handed the work to next, or what the work waits for now. The task as given closes it.
 */

/** Steps that say nothing about the work: a worker starting or resuming, a run's start (its brief is the assignment). */
const QUIET = new Set(['agent.lifecycle', 'agent.started', 'run.started', 'status.working', 'status.ready', 'status.waiting', 'status.needs_approval']);

interface Block {
  run: Execution;
  lead: boolean;
  /** The Lead's runs that took this agent run's result in (woken once it ended). */
  after: Execution[];
  /** What else happened while it was the latest run: people's messages, notes, questions, sub-items. */
  along: Along[];
}

type Along = { at: number; key: string; message?: Message; child?: WorkItem; back?: boolean };

const time = (s: string | null | undefined) => (s ? new Date(s).getTime() : Number.NaN);
const isActive = (r: Execution) => ACTIVE_EXECUTION_STATUSES.includes(r.status);

/** Deskworks' summary when the last sub-item ended; the sub-items stand in the course themselves. */
function childrenDone(m: Message): boolean {
  return (
    m.senderType === 'system' &&
    (m.metadata?.event === 'children_done' || m.metadata?.event === 'child_done' || m.content.startsWith('All child Work Items are done'))
  );
}

/** How a Lead run was woken, from its brief ("Woken by: run.finished, …"), in words. */
export function wokenBy(event: string): string {
  switch (event) {
    case 'run.finished':
      return t('a run ended');
    case 'run.failed':
      return t('a run failed');
    case 'message.received':
      return t('a message');
    case 'work_item.continued':
      return t('it was continued');
    case 'work_item.reopened':
      return t('it was reopened');
    case 'work_item.created':
      return t('it was created');
    case 'work_item.approved':
      return t('it was approved to start');
    case 'approval.resolved':
      return t('a decision');
    case 'question.answered':
      return t('an answer');
    case 'decision.needed':
      return t('a decision to make');
    default:
      return event;
  }
}

export function wokenReasons(brief: string | null | undefined): string {
  return (brief ?? '')
    .replace(/^Woken by:\s*/i, '')
    .split(/[,;]\s*/)
    .map((w) => w.trim())
    .filter(Boolean)
    .map(wokenBy)
    .join(', ');
}

/** The runs of an item as blocks (oldest first), and what happened before the first run. */
export function courseBlocks(item: WorkItemDetail, messages: Message[], runs: Execution[]): { blocks: Block[]; before: Along[] } {
  const own = runs
    .filter((r) => r.workItemId === item.id && !r.sideChat && (r.kind === 'work_item' || r.kind === 'lead'))
    .sort((a, b) => time(a.createdAt) - time(b.createdAt));
  const blocks: Block[] = [];
  for (const r of own) {
    const last = blocks.at(-1);
    if (r.kind === 'lead' && last && !last.lead && last.run.finishedAt && time(r.createdAt) >= time(last.run.finishedAt)) last.after.push(r);
    else blocks.push({ run: r, lead: r.kind === 'lead', after: [], along: [] });
  }
  const ids = new Set(own.map((r) => r.id));
  const leads = new Set(own.filter((r) => r.kind === 'lead').map((r) => r.id));
  const before: Along[] = [];
  const place = (x: Along) => {
    let i = blocks.length - 1;
    while (i >= 0 && time(blocks[i].run.createdAt) > x.at) i--;
    (i >= 0 ? blocks[i].along : before).push(x);
  };
  for (const m of messages) {
    if (childrenDone(m)) continue;
    // The Lead's words and hand-offs stand with its run; a run's result is its finding.
    if (m.senderType === 'agent' && m.executionId && leads.has(m.executionId)) continue;
    if (m.metadata?.event === 'run_finished' && m.executionId && ids.has(m.executionId)) continue;
    place({ at: time(m.createdAt), key: m.id, message: m });
  }
  for (const c of item.children.filter((c) => c.kind !== 'review')) {
    place({ at: time(c.createdAt), key: `${c.id}:out`, child: c, back: false });
    if (c.completedAt) place({ at: time(c.completedAt), key: `${c.id}:back`, child: c, back: true });
  }
  for (const b of blocks) b.along.sort((x, y) => x.at - y.at);
  before.sort((x, y) => x.at - y.at);
  return { blocks, before };
}

/** What the work waits for once the latest run is through, in one line; null while nothing is to say. */
function waitsFor(item: WorkItemDetail): { text: string; you: boolean } | null {
  const lead = item.lead?.name ?? t('the Lead');
  if (item.paused) return { text: t('Paused — it goes on once it is continued.'), you: false };
  switch (item.status) {
    case 'complete':
      return { text: t('Done — the result is at the top.'), you: false };
    case 'cancelled':
      return { text: t('Cancelled.'), you: false };
    case 'failed':
      return { text: t('The work failed.'), you: false };
    case 'blocked':
      return { text: t('Blocked — see the current state at the top.'), you: true };
    case 'needs_approval':
      return { text: t('Waiting for your approval — decide in the card at the top.'), you: true };
    case 'waiting':
      if (item.waitingReason === 'children') return { text: t('Waiting for its sub-items.'), you: false };
      if (item.withLead && !item.questionOpen) return { text: t('{lead} decides the next step', { lead }), you: false };
      return { text: t('Waiting for you — answer in the card at the top.'), you: true };
    case 'ready':
    case 'working':
      return { text: t('The next run starts shortly.'), you: false };
    default:
      return null;
  }
}

export function scrollToRun(runId: string): boolean {
  const el = document.getElementById(`run-${runId}`);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.classList.add('ring-2', 'ring-indigo-300');
  window.setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-300'), 1600);
  return true;
}

export function Course({
  item,
  messages,
  activity,
  runs,
  task,
  live,
  renderMessage,
  renderChild,
}: {
  item: WorkItemDetail;
  messages: Message[];
  activity: ActivityEvent[];
  runs: Execution[];
  /** The task as given: the end of the course, read from the bottom. */
  task: ReactNode;
  /** An answer in the chat being written right now. */
  live?: ReactNode;
  renderMessage: (m: Message) => ReactNode;
  renderChild: (child: WorkItem, back: boolean) => ReactNode;
}) {
  const { blocks, before } = courseBlocks(item, messages, runs);
  const byRun = new Map<string, Message[]>();
  for (const m of messages) {
    if (!m.executionId) continue;
    const list = byRun.get(m.executionId) ?? [];
    list.push(m);
    byRun.set(m.executionId, list);
  }
  const along = (x: Along) =>
    x.child ? <div key={x.key}>{renderChild(x.child, !!x.back)}</div> : <div key={x.key}>{x.message && <AlongMessage message={x.message} render={renderMessage} />}</div>;
  // The agent run a hand-off started: the first run of that agent after it.
  const startedBy = (m: Message) =>
    blocks.find((b) => !b.lead && b.run.agentId === m.metadata?.agentId && time(b.run.createdAt) >= time(m.createdAt))?.run;
  const next = waitsFor(item);

  return (
    <div className="flex flex-col gap-4">
      {live}
      {blocks.length === 0 && next && <NextLine text={next.text} you={next.you} />}
      {[...blocks].reverse().map((b, i) => (
        <RunBlock
          key={b.run.id}
          block={b}
          item={item}
          newest={i === 0}
          next={i === 0 ? next : null}
          activity={activity}
          byRun={byRun}
          startedBy={startedBy}
          along={along}
          renderMessage={renderMessage}
        />
      ))}
      <div className="flex flex-col gap-3">
        <p className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">{t('How it started')}</p>
        {task}
        {before.map(along)}
      </div>
    </div>
  );
}

function RunBlock({
  block,
  item,
  newest,
  next,
  activity,
  byRun,
  startedBy,
  along,
  renderMessage,
}: {
  block: Block;
  item: WorkItemDetail;
  newest: boolean;
  next: { text: string; you: boolean } | null;
  activity: ActivityEvent[];
  byRun: Map<string, Message[]>;
  startedBy: (m: Message) => Execution | undefined;
  along: (x: Along) => ReactNode;
  renderMessage: (m: Message) => ReactNode;
}) {
  const { run, lead, after } = block;
  const running = isActive(run);
  const name = run.agentName ?? (lead ? t('The Lead') : t('Agent'));
  const start = run.startedAt ?? run.createdAt;
  const today = new Date(start).toDateString() === new Date().toDateString();
  const steps = activity.filter((e) => e.executionId === run.id && !QUIET.has(e.type));
  const failed = run.status === 'failed' || run.status === 'lost' || run.outcome === 'failed';
  const finishedMsg = (byRun.get(run.id) ?? []).find((m) => m.metadata?.event === 'run_finished');
  const finding = run.result?.trim() || finishedMsg?.content.trim() || '';

  // What followed: for an agent run the Lead taking it in; for a Lead run, whom it handed the work to.
  const followers = lead ? [] : after;
  const handoffs = (lead ? [run] : after).flatMap((r) => (byRun.get(r.id) ?? []).filter((m) => m.metadata?.event === 'run_started'));
  const followed = followers.some((r) => isActive(r) || r.result?.trim() || (byRun.get(r.id) ?? []).some((m) => m.metadata?.event !== 'run_started'));
  const showNext = newest && !running && !followers.some(isActive) && handoffs.length === 0 && next;

  return (
    <article
      id={`run-${run.id}`}
      aria-label={t('Run of {agent}', { agent: name })}
      className={cn('scroll-mt-3 overflow-hidden rounded-xl border bg-card transition-shadow', newest && 'border-indigo-200 dark:border-indigo-900')}
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3.5 py-2.5 sm:px-4">
        <AgentAvatar seed={run.agentId} size={26} lead={lead} />
        <span className="text-sm font-medium">{name}</span>
        {lead && <span className="rounded-full bg-amber-500/10 px-1.5 py-px text-xs text-amber-800 dark:text-amber-300">{t('Lead')}</span>}
        <span className="text-xs text-muted-foreground" title={formatAbsolute(start, true)}>
          {today ? formatTime(start) : formatAbsolute(start)}
          {!running && run.finishedAt && ` · ${formatDuration(start, run.finishedAt)}`}
        </span>
        {running && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 dark:text-indigo-300">
            <Loader2 className="size-3 animate-spin" /> {t('working')}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <SessionLink executionId={run.id} workspaceId={item.workspaceId} />
        </span>
      </header>

      <Part label={lead ? t('Why') : t('Assignment')}>
        {lead ? (
          <p className="text-[13px]">{wokenReasons(run.brief) ? t('Woken after: {reason}', { reason: wokenReasons(run.brief) }) : t('Woken to take the work on.')}</p>
        ) : run.brief?.trim() ? (
          <Clamp text={run.brief} limit={320} height="max-h-20" />
        ) : (
          <p className="text-[13px] text-muted-foreground">{t('Works on the task as given — see “How it started”.')}</p>
        )}
      </Part>

      {block.along.length > 0 && (
        <Part label={t('Along the way')}>
          <div className="flex flex-col gap-3">{block.along.map(along)}</div>
        </Part>
      )}

      <Part label={lead ? t('Notes') : t('Finding')}>
        {lead ? (
          <LeadWords runs={[run]} byRun={byRun} renderMessage={renderMessage} empty={t('No notes — it handed the work on.')} />
        ) : running ? (
          <AgentSteps executionId={run.id} working agentName={name} />
        ) : (
          <div className="flex flex-col gap-2">
            {failed && run.error && <p className="font-mono text-xs break-words text-red-600">{run.error}</p>}
            {finding ? (
              <div className={cn('rounded-lg border px-3 py-2 text-sm', failed ? 'border-red-200 dark:border-red-900' : 'border-emerald-200 dark:border-emerald-900')}>
                <Clamp text={finding} limit={900} height="max-h-72" />
              </div>
            ) : (
              <p className="text-[13px] text-muted-foreground">{failed ? t('It ended without a result.') : t('No result was saved.')}</p>
            )}
          </div>
        )}
        {!running && steps.length > 0 && <Steps events={steps} workspaceId={item.workspaceId} />}
      </Part>

      {(followed || handoffs.length > 0 || showNext) && (
        <Part label={t('What followed')} tone>
          <div className="flex flex-col gap-2.5">
            {followed && <LeadWords runs={followers} byRun={byRun} renderMessage={renderMessage} />}
            {handoffs.map((m) => {
              const target = startedBy(m);
              const who = typeof m.metadata?.agentName === 'string' ? m.metadata.agentName : t('an agent');
              return target ? (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => scrollToRun(target.id)}
                  className="inline-flex w-fit cursor-pointer items-center gap-1.5 rounded-md bg-indigo-500/10 px-2 py-1 text-[13px] font-medium text-indigo-800 hover:bg-indigo-500/15 dark:text-indigo-200"
                >
                  <ArrowRight className="size-3.5" /> {t('Handed to {agent} — its run is above', { agent: who })}
                </button>
              ) : (
                <span key={m.id} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-indigo-800 dark:text-indigo-200">
                  <ArrowRight className="size-3.5" /> {t('Handed to {agent}', { agent: who })}
                </span>
              );
            })}
            {showNext && <NextLine text={next.text} you={next.you} />}
          </div>
        </Part>
      )}
    </article>
  );
}

/** One part of a run: its label beside (below on a phone) what it holds. */
function Part({ label, tone, children }: { label: string; tone?: boolean; children: ReactNode }) {
  return (
    <div className={cn('grid gap-1.5 border-t px-3.5 py-3 sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-3 sm:px-4', tone && 'bg-indigo-50/40 dark:bg-indigo-950/20')}>
      <span className={cn('pt-0.5 text-[11px] font-semibold tracking-wide uppercase', tone ? 'text-indigo-700 dark:text-indigo-300' : 'text-muted-foreground')}>{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** What the Lead said in its runs (not its hand-offs, which stand below it), and what it concluded. */
function LeadWords({
  runs,
  byRun,
  renderMessage,
  empty,
}: {
  runs: Execution[];
  byRun: Map<string, Message[]>;
  renderMessage: (m: Message) => ReactNode;
  empty?: string;
}) {
  const parts = runs.flatMap((r) => {
    const said = (byRun.get(r.id) ?? []).filter((m) => m.metadata?.event !== 'run_started');
    const out: ReactNode[] = said.map((m) => <div key={m.id}>{renderMessage(m)}</div>);
    if (r.result?.trim()) {
      out.push(
        <div key={`${r.id}:result`} className="rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-sm">
          <Clamp text={r.result} limit={600} height="max-h-40" />
        </div>,
      );
    }
    if (out.length === 0 && isActive(r)) {
      out.push(
        <p key={`${r.id}:busy`} className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> {t('{lead} is taking the result in …', { lead: r.agentName ?? t('The Lead') })}
        </p>,
      );
    }
    return out;
  });
  if (parts.length === 0) return empty ? <p className="text-[13px] text-muted-foreground">{empty}</p> : null;
  return <div className="flex flex-col gap-3">{parts}</div>;
}

/** A message along the way; an action waiting for approval is one line (its card is at the top). */
function AlongMessage({ message: m, render }: { message: Message; render: (m: Message) => ReactNode }) {
  if (m.senderType === 'system' && m.metadata?.event === 'action_requested') {
    const line = m.content.split('\n')[0].replace(/\*\*/g, '').trim();
    return (
      <p className="flex items-start gap-1.5 text-[13px] text-muted-foreground">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-orange-500" />
        <span className="min-w-0 break-words">
          {line} <span className="tabular-nums">· {formatTime(m.createdAt)}</span>
        </span>
      </p>
    );
  }
  return <>{render(m)}</>;
}

function NextLine({ text, you }: { text: string; you: boolean }) {
  return (
    <p className={cn('inline-flex items-start gap-1.5 text-[13px] font-medium', you ? 'text-amber-800 dark:text-amber-300' : 'text-foreground/80')}>
      <Clock className="mt-0.5 size-3.5 shrink-0" /> {text}
    </p>
  );
}

/** The steps a run took, one line each, until opened only their count. */
function Steps({ events, workspaceId }: { events: ActivityEvent[]; workspaceId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        {events.length === 1 ? t('1 step') : t('{count} steps', { count: events.length })}
      </button>
      {open && (
        <ol className="mt-1.5 flex flex-col gap-1.5">
          {events.map((e) => (
            <li key={e.id} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full border bg-card [&_svg]:size-3">{activityIcon(e.type)}</span>
              <span className="min-w-0 flex-1 pt-0.5 break-words">
                <span className="text-foreground/80">{e.message || e.type}</span>{' '}
                <time dateTime={e.createdAt} title={formatAbsolute(e.createdAt, true)} className="tabular-nums">
                  {formatTime(e.createdAt)}
                </time>
                {e.executionId && <SessionLink executionId={e.executionId} workspaceId={workspaceId} className="ml-1.5" />}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** A long text shows its beginning until opened. */
function Clamp({ text, limit, height }: { text: string; limit: number; height: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > limit;
  return (
    <>
      <div className={cn('text-sm', long && !open && `relative overflow-hidden ${height}`)}>
        <Markdown text={text} />
        {long && !open && <div aria-hidden className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent" />}
      </div>
      {long && (
        <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline">
          {open ? t('Show less') : t('Show all')}
        </button>
      )}
    </>
  );
}
